import {CSSProperties} from "react";
import {palette} from "./constants";

// Cap continuous/looping ambient animations to ~30fps via stepped timing.
// N = round(30fps × duration in seconds). One-shot intro animations stay smooth.
const TARGET_FPS = 30;
const stepFor = (sec: number) => `steps(${Math.max(1, Math.round(TARGET_FPS * sec))})`;

const fog = (key: string, style: CSSProperties, wisp: CSSProperties) => (
  <div key={key} style={style}>
    <div style={wisp} />
  </div>
);

export const SplashScene = ({playing}: {playing: boolean}) => {
  return (
    <>
      {/* Night veil */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background: `linear-gradient(180deg, ${palette.night0} 0%, ${palette.night1} 55%, ${palette.night2} 100%)`,
          opacity: 1,
          ...(playing && {animation: "nightLift 3s cubic-bezier(0.4, 0, 0.2, 1) forwards"}),
        }}
      />

      {/* Sun */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "46%",
          zIndex: 2,
          width: 0,
          height: 0,
          transform: "translateY(46vh)",
          ...(playing && {animation: "sunRise 5.2s cubic-bezier(0.3, 0.5, 0.25, 1) forwards"}),
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            translate: "-50% -50%",
            borderRadius: "50%",
            width: "120vmin",
            height: "120vmin",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(252,253,255,0.45) 26%, rgba(238,242,250,0.18) 55%, rgba(238,242,250,0) 72%)",
            ...(playing && {animation: `sunBreathe 7s ${stepFor(7)} 5.2s infinite alternate`}),
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            translate: "-50% -50%",
            borderRadius: "50%",
            width: "52vmin",
            height: "52vmin",
            background: `radial-gradient(circle, ${palette.sunGlow} 0%, rgba(255,255,255,0.72) 38%, rgba(255,255,255,0) 70%)`,
            filter: "blur(6px)",
            ...(playing && {animation: "glowDissolve 2.2s cubic-bezier(0.3, 0.5, 0.25, 1) 2.6s forwards"}),
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            translate: "-50% -50%",
            borderRadius: "50%",
            width: "21vmin",
            height: "21vmin",
            background: "#ffffff",
            filter: "blur(10px)",
            opacity: 0.95,
            ...(playing && {animation: "coreDissolve 2.2s cubic-bezier(0.3, 0.5, 0.25, 1) 2.6s forwards"}),
          }}
        />
      </div>

      {/* Fog — twirling soft wisps */}
      <div
        style={{
          position: "absolute",
          left: "-12%",
          right: "-12%",
          top: "-12%",
          bottom: "16%",
          zIndex: 2,
          pointerEvents: "none",
          opacity: 0,
          ...(playing && {animation: "fadeIn 2.6s ease-out 2s forwards"}),
        }}
      >
        {[
          {
            left: "18%",
            top: "20%",
            anim: `fogSpin 64s ${stepFor(64)} infinite`,
            w: "110vmin",
            h: "64vmin",
            c: `color-mix(in srgb, ${palette.mist} 88%, transparent)`,
            fend: 0.5,
            ox: "9vmin",
            oy: "-5vmin",
          },
          {
            left: "84%",
            top: "14%",
            anim: `fogSpin 50s ${stepFor(50)} infinite reverse`,
            w: "95vmin",
            h: "60vmin",
            c: `color-mix(in srgb, ${palette.duskLow} 82%, transparent)`,
            fend: 0.45,
            ox: "-10vmin",
            oy: "-5vmin",
          },
          {
            left: "50%",
            top: "36%",
            anim: `fogSway 17s ${stepFor(17)} infinite alternate`,
            w: "90vmin",
            h: "52vmin",
            c: "rgba(255,255,255,0.78)",
            fend: 0.12,
            ox: "9vmin",
            oy: "6vmin",
          },
          {
            left: "4%",
            top: "58%",
            anim: `fogSway 23s ${stepFor(23)} infinite alternate-reverse`,
            w: "84vmin",
            h: "56vmin",
            c: `color-mix(in srgb, ${palette.duskTop} 55%, transparent)`,
            fend: 0.38,
            ox: "9vmin",
            oy: "-5vmin",
          },
          {
            left: "94%",
            top: "62%",
            anim: `fogSpin 74s ${stepFor(74)} infinite`,
            w: "88vmin",
            h: "54vmin",
            c: `color-mix(in srgb, ${palette.mist} 75%, transparent)`,
            fend: 0.32,
            ox: "-8vmin",
            oy: "-5vmin",
          },
        ].map((s, i) =>
          fog(
            `swirl-${i}`,
            {
              position: "absolute",
              width: 0,
              height: 0,
              left: s.left,
              top: s.top,
              ...(playing && {animation: s.anim}),
            },
            {
              position: "absolute",
              left: s.ox,
              top: s.oy,
              width: s.w,
              height: s.h,
              translate: "-50% -50%",
              borderRadius: "50%",
              background: `radial-gradient(closest-side, ${s.c} 0%, transparent 72%)`,
              filter: "blur(18px)",
              willChange: "transform, opacity",
              ["--fend" as string]: s.fend,
              ...(playing && {animation: "wispClear 5.4s ease-in-out 2.8s forwards"}),
            }
          )
        )}
      </div>

      {/* Peach bloom */}
      <div
        style={{
          position: "absolute",
          left: "-10%",
          right: "-10%",
          bottom: "6%",
          height: "52%",
          zIndex: 2,
          pointerEvents: "none",
          opacity: 0,
          background: `radial-gradient(ellipse 85% 75% at 50% 92%, color-mix(in srgb, ${palette.horizon} 88%, transparent) 0%, color-mix(in srgb, ${palette.horizon} 38%, transparent) 48%, transparent 78%)`,
          filter: "blur(8px)",
          ...(playing && {animation: "peachBloom 3.2s ease-in-out 3.4s forwards"}),
        }}
      />

      {/* Hills — bounce "n" arcs */}
      <svg
        viewBox="0 0 390 260"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "30vh",
          zIndex: 3,
          display: "block",
          width: "100%",
        }}
      >
        <g>
          <path
            d="M0 98 A 520 520 0 0 1 390 90 L390 260 L0 260 Z"
            fill={palette.hillFar}
            style={{
              opacity: 0,
              ["--fo" as string]: 0.5,
              ...(playing && {animation: "fillDissolve 2.1s ease-out calc(0.9s + 1.15s) forwards"}),
            }}
          />
          <path
            d="M0 98 A 520 520 0 0 1 390 90"
            fill="none"
            stroke="rgba(255,249,238,0.95)"
            strokeWidth={1.4}
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            pathLength={1}
            style={{
              strokeDasharray: 1,
              strokeDashoffset: 1,
              opacity: 0,
              ...(playing && {
                animation:
                  "crestDraw 1.7s cubic-bezier(0.45, 0, 0.3, 1) 0.9s forwards, crestSettle 1.8s ease-out calc(0.9s + 2.1s) forwards",
              }),
            }}
          />
        </g>
        <g>
          <path
            d="M0 150 A 340 340 0 0 1 390 142 L390 260 L0 260 Z"
            fill={palette.hillMid}
            style={{
              opacity: 0,
              ["--fo" as string]: 0.68,
              ...(playing && {animation: "fillDissolve 2.1s ease-out calc(1.35s + 1.15s) forwards"}),
            }}
          />
          <path
            d="M0 150 A 340 340 0 0 1 390 142"
            fill="none"
            stroke="rgba(255,249,238,0.95)"
            strokeWidth={1.4}
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            pathLength={1}
            style={{
              strokeDasharray: 1,
              strokeDashoffset: 1,
              opacity: 0,
              ...(playing && {
                animation:
                  "crestDraw 1.7s cubic-bezier(0.45, 0, 0.3, 1) 1.35s forwards, crestSettle 1.8s ease-out calc(1.35s + 2.1s) forwards",
              }),
            }}
          />
        </g>
      </svg>

      {/* Mist band */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "16vh",
          height: "12vh",
          zIndex: 4,
          background: `radial-gradient(ellipse 70% 100% at 50% 50%, color-mix(in srgb, ${palette.mist} 70%, transparent) 0%, transparent 70%)`,
          filter: "blur(10px)",
          opacity: 0,
          ...(playing && {animation: "fadeIn 2.4s ease-out 1.8s forwards"}),
        }}
      />
    </>
  );
};
